import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../../../lib/api";
import { reasoningLabel } from "../../../lib/reasoning";
import { SavedNote } from "../../shared/Icons";
import { toastError } from "../../shared/toast";
import {
  Pill, Toggle, btnSm, card, errorText, fmtDateTime, fmtInt, fmtUsd, input, label, num, primaryBtn, td, th,
} from "./common";

/** Leitura das conversas por IA (alimenta os blocos de IA da dash do cliente):
 *  config global + liga/desliga e "Analisar agora" por agente. */

interface Config {
  provider: string;
  model: string;
  reasoning: string;
  daily_cap_usd: number;
  models: { provider: string; model: string; label: string }[];
}

type RunStatus = "running" | "ok" | "cap" | "error" | "no_model";

interface AgentRow {
  agent_id: string;
  agent_name: string;
  accounts: string[];
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: RunStatus | null;
  last_run_read: number | null;
  last_run_error: string | null;
  analyzed_leads: number;
  cost_today_usd: number;
}

const REASONING = ["off", "low", "medium", "high"];
const PROVIDER_LABEL: Record<string, string> = { google: "Google (Gemini)", openai: "OpenAI" };
// "Analisar agora" fica "na fila" até a rodada começar/terminar; desiste em 5 min.
const PENDING_MS = 5 * 60_000;

const STATUS: Record<RunStatus, { text: string; tone: "ok" | "warn" | "danger" | "info" }> = {
  running: { text: "Analisando…", tone: "info" },
  ok: { text: "OK", tone: "ok" },
  cap: { text: "Teto atingido", tone: "warn" },
  error: { text: "Erro", tone: "danger" },
  no_model: { text: "Sem modelo", tone: "warn" },
};

export function AnalysisTab() {
  return (
    <div style={{ maxWidth: 1100, display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>
          Análise IA das conversas
        </div>
        <div style={{ fontSize: 12.5, color: "var(--ink-muted)", lineHeight: 1.5 }}>
          A cada hora, lê as conversas paradas há 2h ou mais dos agentes ligados a contas ativas
          do portal e preenche os blocos de IA da dash do cliente (origem, intenção, visitas,
          pagamento…). Conversa só com humano não gasta leitura. Agente desligado sai da rodada
          de hora em hora; "Analisar agora" lê na hora, inclusive conversas recentes.
        </div>
      </div>
      <ConfigCard />
      <AgentsTable />
    </div>
  );
}

function ConfigCard() {
  const qc = useQueryClient();
  const { data: cfg, isLoading } = useQuery<Config>({
    queryKey: ["helena-analysis-config"],
    queryFn: () => api.get("/dev/portal-analysis/config"),
  });

  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [reasoning, setReasoning] = useState("off");
  const [cap, setCap] = useState("2");
  const [loaded, setLoaded] = useState(false);

  // Hidrata o form quando o GET retorna (uma vez).
  useEffect(() => {
    if (!cfg || loaded) return;
    setProvider(cfg.provider || cfg.models[0]?.provider || "");
    setModel(cfg.model);
    setReasoning(cfg.reasoning || "off");
    setCap(String(cfg.daily_cap_usd));
    setLoaded(true);
  }, [cfg, loaded]);

  const providers = [...new Set((cfg?.models ?? []).map((m) => m.provider))];
  const providerModels = (cfg?.models ?? []).filter((m) => m.provider === provider);
  const capNum = Number(cap.replace(",", "."));
  const capOk = cap.trim() !== "" && Number.isFinite(capNum) && capNum >= 0;

  const mut = useMutation({
    mutationFn: () => {
      // PATCH só com o que mudou; provedor e modelo andam juntos.
      const body: Record<string, unknown> = {};
      if (cfg) {
        if (provider !== cfg.provider || model !== cfg.model) { body.provider = provider; body.model = model; }
        if (reasoning !== cfg.reasoning) body.reasoning = reasoning;
        if (capNum !== cfg.daily_cap_usd) body.daily_cap_usd = capNum;
      }
      return api.patch<Config>("/dev/portal-analysis/config", body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["helena-analysis-config"] }),
    onError: (e) => toastError(errorText(e, "Falha ao salvar a configuração da análise.")),
  });

  function changeProvider(p: string) {
    setProvider(p);
    setModel(cfg?.models.find((m) => m.provider === p)?.model ?? "");
  }

  if (isLoading || !cfg) {
    return <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando…</div>;
  }

  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Configuração</div>
      {cfg.models.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--warn)" }}>
          Nenhum modelo Google ou OpenAI em Modelos Disponíveis — cadastre um para ligar a análise.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 150px" }}>
            <label style={label}>Provedor</label>
            <select style={input} value={provider} onChange={(e) => changeProvider(e.target.value)}>
              {provider && !providers.includes(provider) && <option value={provider}>{provider}</option>}
              {providers.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p] ?? p}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "2 1 220px" }}>
            <label style={label}>Modelo</label>
            <select style={input} value={model} onChange={(e) => setModel(e.target.value)}>
              {/* Sem placeholder o select mostraria o 1º modelo sem ele estar escolhido. */}
              {!model && <option value="">Selecione…</option>}
              {model && !providerModels.some((m) => m.model === model) && <option value={model}>{model}</option>}
              {providerModels.map((m) => <option key={m.model} value={m.model}>{m.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 130px" }}>
            <label style={label}>Raciocínio</label>
            <select style={input} value={reasoning} onChange={(e) => setReasoning(e.target.value)}>
              {REASONING.map((r) => <option key={r} value={r}>{reasoningLabel(r)}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 150px" }}>
            <label style={label}>Teto diário por agente (US$)</label>
            <input style={input} type="number" min={0} step={0.5} value={cap} onChange={(e) => setCap(e.target.value)} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              style={{ ...primaryBtn, opacity: model && capOk ? 1 : 0.5 }}
              disabled={!model || !capOk || mut.isPending}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? "Salvando…" : "Salvar"}
            </button>
            <SavedNote show={mut.isSuccess} />
          </div>
        </div>
      )}
      {!cfg.model && cfg.models.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--warn)" }}>
          Sem modelo salvo: a análise não roda (status "Sem modelo") até salvar um.
        </div>
      )}
    </div>
  );
}

function AgentsTable() {
  const qc = useQueryClient();
  // agent_id → quando clicou + last_run_at daquele momento (detecta a rodada nova).
  const [pending, setPending] = useState<Record<string, { at: number; lastRunAt: string | null }>>({});

  const isQueued = (a: AgentRow) => {
    const p = pending[a.agent_id];
    return !!p && Date.now() - p.at < PENDING_MS && a.last_run_status !== "running" && a.last_run_at === p.lastRunAt;
  };

  const { data: agents = [], isLoading } = useQuery<AgentRow[]>({
    queryKey: ["helena-analysis-agents"],
    queryFn: () => api.get("/dev/portal-analysis/agents"),
    // Enquanto alguma rodada roda (ou foi pedida agora), atualiza a cada 10s.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((a) => a.last_run_status === "running" || isQueued(a)) ? 10_000 : false,
  });

  const { data: cfg } = useQuery<Config>({
    queryKey: ["helena-analysis-config"],
    queryFn: () => api.get("/dev/portal-analysis/config"),
  });
  const hasModel = !!cfg?.model;

  const toggleMut = useMutation({
    mutationFn: (a: AgentRow) =>
      api.patch(`/dev/portal-analysis/agents/${a.agent_id}`, { enabled: !a.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["helena-analysis-agents"] }),
    onError: (e) => toastError(errorText(e, "Não foi possível alterar o agente.")),
  });

  const runMut = useMutation({
    mutationFn: (a: AgentRow) => api.post(`/dev/portal-analysis/agents/${a.agent_id}/run`, {}),
    onSuccess: (_r, a) => {
      setPending((p) => ({ ...p, [a.agent_id]: { at: Date.now(), lastRunAt: a.last_run_at } }));
      qc.invalidateQueries({ queryKey: ["helena-analysis-agents"] });
    },
    onError: (e) => toastError(errorText(e, "Não foi possível iniciar a análise.")),
  });

  if (isLoading) return <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando…</div>;
  if (agents.length === 0) {
    return (
      <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>
        Nenhum agente em conta ativa do portal — crie uma conta na sub-aba Contas.
      </div>
    );
  }

  const cap = cfg?.daily_cap_usd ?? null;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>
          <th style={th}>Agente</th>
          <th style={th}>Contas</th>
          <th style={th}>Ligado</th>
          <th style={th}>Última leitura</th>
          <th style={{ ...th, textAlign: "right" }}>Leads analisados</th>
          <th style={{ ...th, textAlign: "right" }}>Gasto hoje</th>
          <th style={th} />
        </tr>
      </thead>
      <tbody>
        {agents.map((a) => {
          const running = a.last_run_status === "running";
          const queued = isQueued(a);
          const st = a.last_run_status ? STATUS[a.last_run_status] : null;
          const atCap = cap != null && cap > 0 && a.cost_today_usd >= cap;
          // Desligado só sai da rodada de hora em hora; a leitura manual segue valendo.
          const blockReason = !hasModel ? "Salve um modelo na configuração acima"
            : running || queued ? "Já está analisando" : undefined;
          return (
            <tr key={a.agent_id} style={{ color: "var(--ink)" }}>
              <td style={{ ...td, fontWeight: 500 }}>{a.agent_name}</td>
              <td style={{ ...td, color: "var(--ink-muted)", maxWidth: 240 }}>
                <span title={a.accounts.join(", ")} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.accounts.join(", ")}
                </span>
              </td>
              <td style={td}>
                <Toggle
                  on={a.enabled}
                  disabled={toggleMut.isPending}
                  title={a.enabled ? "Tirar da leitura de hora em hora" : "Voltar para a leitura de hora em hora"}
                  onChange={() => toggleMut.mutate(a)}
                />
              </td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: a.last_run_at ? "var(--ink)" : "var(--ink-dim)" }}>
                    {a.last_run_at ? fmtDateTime(a.last_run_at) : "nunca"}
                  </span>
                  {queued ? <Pill text="Na fila…" tone="info" />
                    : st && <Pill text={st.text} tone={st.tone} title={a.last_run_status === "error" ? a.last_run_error ?? undefined : undefined} />}
                </div>
                {a.last_run_read != null && !running && (
                  <div style={{ fontSize: 11.5, color: "var(--ink-muted)", marginTop: 2 }}>
                    {fmtInt(a.last_run_read)} {a.last_run_read === 1 ? "conversa lida" : "conversas lidas"}
                  </div>
                )}
              </td>
              <td style={{ ...td, ...num }}>{fmtInt(a.analyzed_leads)}</td>
              <td style={{ ...td, ...num, color: atCap ? "var(--warn)" : undefined }} title={atCap ? "Teto diário atingido" : undefined}>
                {fmtUsd(a.cost_today_usd)}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                <button
                  style={{ ...btnSm, opacity: blockReason ? 0.5 : 1 }}
                  disabled={!!blockReason || runMut.isPending}
                  title={blockReason}
                  onClick={() => runMut.mutate(a)}
                >
                  Analisar agora
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
